import { Link } from 'react-router-dom';
import { useSession, activeMembership } from '../lib/store';
import { Card, Notice } from '../components/Shell';
import { Stamp } from '../components/Stamp';
import { PlatformHome } from './PlatformHome';
import { money, minutesToTime, rateSuffix } from '../lib/format';
import type { ServiceSummary } from '../lib/types';

/**
 * The public page for one sitting business.
 *
 * The hero is a filled-in record card rather than a headline over a photo,
 * because the card is the product: the whole pitch is that the seven-page paper
 * pack becomes this, and it cannot be lost or left at home.
 */
export function Storefront() {
  const { tenant, tenantError, user } = useSession();
  const membership = activeMembership(useSession());

  // A slug that resolves to nothing is an error worth saying plainly. The
  // platform host is not an error: it belongs to no sitter, and it is the
  // product's own front door.
  if (tenantError) {
    return (
      <div className="mx-auto max-w-xl px-5 py-24">
        <h1 className="text-3xl">No business here yet</h1>
        <p className="mt-3 text-[color:var(--color-ink-soft)]">{tenantError}</p>
        <div className="mt-6 flex gap-3">
          <Link to="/start-a-business" className="btn btn-primary">
            Set up my business
          </Link>
          <Link to="/sign-in" className="btn btn-secondary">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  if (!tenant) return <PlatformHome />;

  const services = tenant.services;

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24">
      {/* --- Hero: the record card is the thesis --------------------------- */}
      <section className="grid items-center gap-10 py-14 md:grid-cols-[1.05fr_0.95fr] md:py-20">
        <div className="min-w-0">
          <p className="eyebrow mb-4">
            {tenant.serviceArea ? `Pet care in ${tenant.serviceArea}` : 'In-home pet care'}
          </p>
          <h1 className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
            {tenant.branding.tagline ?? (
              <>
                Your pet&rsquo;s whole file,
                <br />
                <span className="text-[color:var(--color-stamp)]">kept properly.</span>
              </>
            )}
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-[color:var(--color-ink-soft)]">
            {tenant.about ??
              `Book boarding, day care and visits with ${tenant.businessName}. Feeding times, medication, vaccine records and your signed agreement all live in one place, so nothing gets printed, lost or asked for twice.`}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {membership ? (
              <Link to="/portal/book" className="btn btn-primary">
                Book a stay
              </Link>
            ) : (
              <Link to={user ? '/portal' : '/sign-up'} className="btn btn-primary">
                {user ? 'Open my portal' : 'Start my pet\u2019s file'}
              </Link>
            )}
            <a href="#services" className="btn btn-secondary">
              See rates
            </a>
          </div>

          {tenant.credentials.length > 0 && (
            <ul className="mt-9 flex flex-wrap gap-x-3 gap-y-3">
              {tenant.credentials.map((credential) => (
                <li key={credential}>
                  <Stamp tone="pending" flat>
                    {credential}
                  </Stamp>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* A sitter's own photograph beats the illustration every time, and
            most of them have one: it is the picture already on their old site.
            The card is the fallback, not the other way round.

            The aspect ratio is fixed here because the photograph arrives
            straight off a phone. Left unconstrained, a portrait shot towered
            over the headline beside it and pushed the buttons below the fold. */}
        {tenant.branding.heroImageUrl ? (
          <img
            src={tenant.branding.heroImageUrl}
            alt={`${tenant.businessName} pet care`}
            className="aspect-[4/3] w-full rounded-[var(--radius-card)] border border-[color:var(--color-rule)] bg-[color:var(--color-sheet-edge)] object-cover shadow-sm"
          />
        ) : (
          <RecordCardHero businessName={tenant.businessName} />
        )}
      </section>

      {/* --- Services ------------------------------------------------------ */}
      <section id="services" className="scroll-mt-8 border-t border-[color:var(--color-rule)] pt-14">
        <header className="mb-8 max-w-2xl">
          <p className="eyebrow mb-2">Services and rates</p>
          <h2 className="text-3xl">What {tenant.businessName} offers</h2>
          <p className="mt-3 text-[color:var(--color-ink-soft)]">
            Every rate below is what you will be quoted. Holiday dates, extra pets and add-ons are
            itemised before you confirm, never added afterwards, and never a surprise on the day
            you come to collect them.
          </p>
        </header>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.serviceId} service={service} />
          ))}
        </div>

        {services.length === 0 && (
          <Notice tone="warn">
            {tenant.businessName} has not published any services yet. Get in touch at{' '}
            <a className="underline" href={`mailto:${tenant.publicEmail}`}>
              {tenant.publicEmail}
            </a>
            .
          </Notice>
        )}
      </section>

      {/* --- How booking works. A real sequence, so it is numbered. -------- */}
      <section className="mt-20 border-t border-[color:var(--color-rule)] pt-14">
        <h2 className="mb-8 text-3xl">How booking works</h2>
        <ol className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: 'Fill in the file',
              body: 'Your details, your pet, feeding times, medication, vet. Once, not every trip.',
            },
            {
              title: 'Upload the vaccine card',
              body: 'A photo is fine. We track the expiry so you are told before it lapses.',
            },
            {
              title: 'Sign the agreement',
              body: `The ${tenant.businessName} services agreement, signed on your phone. No printer.`,
            },
            {
              title: 'Book and pay',
              body: `Pick your dates, see the full price, pay by card${
                tenant.acceptedPaymentMethods.some((m) => m !== 'card') ? ' or however you usually do' : ''
              }.`,
            },
          ].map((step, index) => (
            <li key={step.title}>
              <span className="font-mono text-xs tracking-[0.2em] text-[color:var(--color-stamp)]">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-2 font-display text-lg font-bold">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--color-ink-soft)]">{step.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* --- Contact ------------------------------------------------------- */}
      <section className="mt-20 border-t border-[color:var(--color-rule)] pt-14">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <h2 className="text-2xl">Questions before you book?</h2>
            <p className="mt-2 text-[color:var(--color-ink-soft)]">
              Every booking starts with a free meet and greet.
            </p>
          </div>
          <dl className="text-sm">
            <div className="flex gap-3">
              <dt className="field-label mb-0 w-16">Email</dt>
              <dd>
                <a className="typed underline" href={`mailto:${tenant.publicEmail}`}>
                  {tenant.publicEmail}
                </a>
              </dd>
            </div>
            {tenant.publicPhone && (
              <div className="mt-1 flex gap-3">
                <dt className="field-label mb-0 w-16">Phone</dt>
                <dd>
                  <a className="typed underline" href={`tel:${tenant.publicPhone}`}>
                    {tenant.publicPhone}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* The sitter's own domain is the address they think of as theirs, so
            it is where they try to sign in. The header offers one door for
            both roles, but everything around it is written to a pet owner, so
            the way in is named here too. Signed in, the header already carries
            whichever side of the business they belong on. */}
        <p className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-xs text-[color:var(--color-ink-faint)]">
          <span>Cancel free with {tenant.cancellationNoticeDays} days notice.</span>
          {!user && (
            <Link to="/sign-in" className="underline">
              Sitter and staff sign-in
            </Link>
          )}
        </p>
      </section>
    </div>
  );
}

/**
 * A record card, partly filled in. This is the hero image: it shows what the
 * product actually is in one glance, and it doubles as a legend for the stamp
 * device used throughout the app.
 */
function RecordCardHero({ businessName }: { businessName: string }) {
  return (
    // min-w-0 lets this grid item shrink; the pr-3 keeps the offset carbon
    // sheets behind it from poking past the container edge.
    <div className="relative min-w-0 pr-3">
      {/* Two sheets peeking out behind, as a carbon-copy set does. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 translate-x-3 translate-y-3 rotate-1 border border-[color:var(--color-rule)] bg-[color:var(--color-copy-pink)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 translate-x-1.5 translate-y-1.5 rotate-[0.5deg] border border-[color:var(--color-rule)] bg-[color:var(--color-copy-yellow)]"
      />

      <Card tab="Pet record" className="relative p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="field-label">Name</p>
            <p className="font-display text-3xl font-extrabold">Biscuit</p>
            <p className="typed mt-1 text-sm text-[color:var(--color-ink-soft)]">
              Cocker spaniel, 4 yrs, female
            </p>
          </div>
          <div className="shrink-0 pt-2">
            <Stamp tone="ok">Vaccines current</Stamp>
          </div>
        </div>

        <dl className="mt-6">
          {[
            ['Feeds', '7:00am and 5:30pm, 1 cup'],
            ['Medication', 'Apoquel 16mg, with breakfast'],
            ['Loves', 'The green squeaky frog'],
            ['Hates', 'The hoover, thunder'],
            ['Bedtime', 'Crate at 9pm, door left open'],
          ].map(([label, value]) => (
            <div
              key={label}
              className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-[color:var(--color-rule)] py-2 last:border-b-0"
            >
              <dt className="field-label mb-0">{label}</dt>
              <dd className="typed min-w-0 text-sm">{value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-[color:var(--color-rule)] pt-4">
          <Stamp tone="pending">Agreement signed</Stamp>
          <span className="font-mono text-[0.65rem] tracking-wider text-[color:var(--color-ink-faint)]">
            {businessName.toUpperCase()}
          </span>
        </div>
      </Card>
    </div>
  );
}

function ServiceCard({ service }: { service: ServiceSummary }) {
  const isQuoted = service.rateType === 'quote';
  const isFree = service.basePrice === 0 && !isQuoted;

  return (
    <article className="card flex h-full flex-col p-5">
      <h3 className="font-display text-xl font-bold">{service.name}</h3>

      <p className="mt-3">
        {isQuoted ? (
          <span className="typed text-[color:var(--color-ink-soft)]">Quoted per trip</span>
        ) : isFree ? (
          <span className="font-display text-2xl font-extrabold text-[color:var(--color-field)]">Free</span>
        ) : (
          <>
            <span className="font-display text-3xl font-extrabold">{money(service.basePrice)}</span>{' '}
            <span className="text-sm text-[color:var(--color-ink-soft)]">{rateSuffix(service.rateType)}</span>
          </>
        )}
      </p>

      <p className="mt-3 flex-1 text-sm leading-relaxed text-[color:var(--color-ink-soft)]">
        {service.description}
      </p>

      <dl className="mt-4 space-y-1 border-t border-[color:var(--color-rule)] pt-3 font-mono text-xs text-[color:var(--color-ink-faint)]">
        {service.petsIncluded > 1 && service.petsIncluded < 90 && (
          <div>Covers up to {service.petsIncluded} pets</div>
        )}
        {service.additionalPetPrice > 0 && (
          <div>{money(service.additionalPetPrice)} for each extra pet</div>
        )}
        {service.windowStartMinutes !== undefined && service.windowEndMinutes !== undefined && (
          <div>
            {minutesToTime(service.windowStartMinutes)} to {minutesToTime(service.windowEndMinutes)}
          </div>
        )}
        {service.durationMinutes !== undefined && <div>{service.durationMinutes} minutes</div>}
        {service.requiresAssessment && <div>Assessment required first</div>}
      </dl>
    </article>
  );
}
